/**
 * Allowlist of OpenAI models Alice may use, shared by the web UI (to render the
 * AI Settings selector + chat-input switcher) and the Worker (to validate an
 * incoming model before calling OpenAI). Frontend-safe: labels + model ids only,
 * never API keys.
 *
 * Model ids must be ones the OpenAI account can call. If the account lacks
 * access to a given model, the Worker gracefully falls back (see openai.ts
 * MODEL_FALLBACKS + shouldRetryWithNextModel). GPT-5 / o-series models are
 * reasoning models — the Worker omits the custom temperature for them.
 *
 * Tiers are intentionally simple. The internal One Up portal exposes the full
 * list; client/sandbox portals should be restricted to a cheaper subset in a
 * later pass (per-portal model policy).
 */

export type AiModelId = 'max' | 'pro' | 'fast' | 'stable';

export interface AiModelOption {
  /** Stable key stored as the user preference. */
  id: AiModelId;
  /** User-facing label shown in Settings and the Alice toolbar. */
  label: string;
  /** Short helper text. */
  description: string;
  /** The OpenAI model id sent to the API. */
  model: string;
}

export const AI_MODEL_OPTIONS = [
  {
    id: 'max',
    label: 'Max — GPT-5.5',
    description: 'Newest frontier model. Most capable for hard reasoning, drafting, and analysis.',
    model: 'gpt-5.5',
  },
  {
    id: 'pro',
    label: 'Pro — GPT-5.4',
    description: 'Frontier model tuned for professional work. Strong default for most tasks.',
    model: 'gpt-5.4',
  },
  {
    id: 'fast',
    label: 'Fast — GPT-5.4 mini',
    description: 'Quicker, lower-cost replies for quick lookups and short drafts.',
    model: 'gpt-5.4-mini',
  },
  {
    id: 'stable',
    label: 'Stable — GPT-4o',
    description: 'Known-good fallback if a newer model is unavailable or misbehaves.',
    model: 'gpt-4o',
  },
] as const satisfies readonly AiModelOption[];

export const DEFAULT_AI_MODEL_ID: AiModelId = 'pro';
export const DEFAULT_AI_MODEL = 'gpt-5.4';

export function getAiModelOption(id: string | undefined): AiModelOption | undefined {
  return AI_MODEL_OPTIONS.find((option) => option.id === id);
}

/** Always returns a usable option, falling back to the configured default. */
export function resolveAiModelOption(id: string | undefined): AiModelOption {
  return (
    getAiModelOption(id) ??
    getAiModelOption(DEFAULT_AI_MODEL_ID) ??
    AI_MODEL_OPTIONS[0]
  );
}

export function getAiModelLabel(id: string | undefined): string {
  return resolveAiModelOption(id).label;
}

/** Validate an OpenAI model id against the allowlist (Worker-side use). */
export function isAllowedAiModel(model: string | undefined | null): boolean {
  return Boolean(model && AI_MODEL_OPTIONS.some((option) => option.model === model));
}

/** Resolve an incoming model value to an allowed model, falling back to default. */
export function resolveAiModel(model: string | undefined | null): string {
  return isAllowedAiModel(model) ? (model as string) : DEFAULT_AI_MODEL;
}
